import { AdSlotContainerItemView } from './ad-slot-container-item.view';
import cogIcon from '../assets/cog.svg';
import logoIcon from '../assets/logo.svg';
import { BrowserObjectModel } from '../components/browser-object-model';
import { Injectable, Optional } from '../components/dependency-injection/decorators';
import { AdSlotContainerViewEvent } from '../components/event-manager/event/ad-slot-container-view.event';
import { Events } from '../components/event-manager/event/events';
import { EventManager } from '../components/event-manager/event-manager';
import { Logger } from '../components/logger/logger';
import { StylesheetRenderer } from '../components/renderer/stylesheet.renderer';
import { ViewObserver } from '../components/renderer/utils/view-observer';
import { LinkingTypeEnum } from '../dto/recs.dto';
import { AdSlot } from '../entities/ad-slot';
import { AdSlotContainer } from '../entities/ad-slot-container';
import { AdSlotContainerItem } from '../entities/ad-slot-container-item';

@Injectable()
export class AdSlotContainerView {
  public constructor (
    private readonly browserObjectModel: BrowserObjectModel,
    private readonly adSlotContainerItemView: AdSlotContainerItemView,
    private readonly eventManager: EventManager,
    private readonly stylesheetRenderer: StylesheetRenderer,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public async render (adSlotContainer: AdSlotContainer): Promise<Element> {
    const adSlot = adSlotContainer.getAdSlot();

    if (!adSlot) {
      throw new Error('AdSlot is required to render AdSlotContainer');
    }

    const innerContainerElement = this.browserObjectModel.getDocument().createElement('div');
    innerContainerElement.setAttribute('data-id', `${adSlot.getId()}-${adSlotContainer.getIndex()}`);
    innerContainerElement.className = this.stylesheetRenderer.getClass(adSlot, 'container');

    const headingText = (adSlot.getSettings().getHeadingText() ?? 'We recommend').trim();

    const headingElement = this.browserObjectModel.getDocument().createElement('div');
    headingElement.className = this.stylesheetRenderer.getClass(adSlot, 'heading');
    headingElement.textContent = headingText !== '' ? headingText : 'We recommend';

    innerContainerElement.appendChild(headingElement);

    const viewObserver = ViewObserver.getInstance({
      trackVisibility: false,
      delay: 100,
    });

    const itemsByLinkingType: Record<LinkingTypeEnum, AdSlotContainerItem[]> = adSlotContainer
      .getItems()
      .reduce((acc, item) => {
        const linkingType = item.getLinkingType();

        if (linkingType === null) {
          return acc;
        }

        if (!acc[ linkingType ]) {
          acc[ linkingType ] = [];
        }

        acc[ linkingType ].push(item);

        return acc;
      }, {} as Record<LinkingTypeEnum, AdSlotContainerItem[]>);

    const sectionsElement = this.browserObjectModel.getDocument().createElement('div');
    sectionsElement.className = this.stylesheetRenderer.getClass(adSlot, 'sections');

    for (const [ linkingType, items ] of Object.entries(itemsByLinkingType)) {
      const sectionElement = this.browserObjectModel.getDocument().createElement('div');
      sectionElement.setAttribute('data-section-linking-type', linkingType.toString());
      sectionElement.className = this.stylesheetRenderer.getClass(adSlot, 'section');

      const listElement = this.browserObjectModel.getDocument().createElement('ul');
      listElement.className = this.stylesheetRenderer.getClass(adSlot, 'list');

      for (const item of items) {
        const itemElement = await this.adSlotContainerItemView.render(item, viewObserver);
        listElement.appendChild(itemElement);
      }

      sectionElement.appendChild(listElement);
      sectionsElement.appendChild(sectionElement);
    }

    innerContainerElement.appendChild(sectionsElement);
    innerContainerElement.appendChild(this.getFooterElement(adSlot));

    viewObserver.observe(innerContainerElement, {
      freezeOnceVisible: true,
      minimumVisibleTime: adSlot?.getSettings().getImpressionTrackTimeout() ?? 0,
      threshold: adSlot?.getSettings().getImpressionMinimumVisibleThreshold() ?? 0.01,
      callback: async (element, isInView) => {
        if (!isInView) {
          return;
        }

        await this.eventManager.dispatch(
          Events.AD_SLOT_CONTAINER_VIEW,
          new AdSlotContainerViewEvent(adSlotContainer, element),
        );
      },
    });

    return innerContainerElement;
  }

  private getFooterElement (adSlot: AdSlot): Element {
    const logoElement = this.browserObjectModel.getDocument().createElement('span');
    logoElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerLogo');
    logoElement.innerHTML = logoIcon;

    const brandLinkElement = this.browserObjectModel.getDocument().createElement('a');
    brandLinkElement.href = adSlot.getSettings().getLinkProperties()?.brand?.href ?? 'https://www.trendmd.com/how-it-works-readers';
    brandLinkElement.target = adSlot.getSettings().getLinkProperties()?.brand?.target ?? '_blank';
    brandLinkElement.rel = adSlot.getSettings().getLinkProperties()?.brand?.rel ?? '';
    brandLinkElement.referrerPolicy = adSlot.getSettings().getLinkProperties()?.brand?.referrerPolicy ?? '';
    brandLinkElement.insertAdjacentHTML('beforeend', `<span>Powered by</span>`);
    brandLinkElement.appendChild(logoElement);

    const leftElement = this.browserObjectModel.getDocument().createElement('div');
    leftElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerLeft');
    leftElement.appendChild(brandLinkElement);

    const rightElement = this.browserObjectModel.getDocument().createElement('div');
    rightElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerRight');

    const settingsElement = this.getSettingsElement(adSlot);
    rightElement.appendChild(settingsElement);

    const footerElement = this.browserObjectModel.getDocument().createElement('div');
    footerElement.className = this.stylesheetRenderer.getClass(adSlot, 'footer');
    footerElement.appendChild(leftElement);
    footerElement.appendChild(rightElement);

    return footerElement;
  }

  private getSettingsElement (adSlot: AdSlot): Element {
    const settingsElement = this.browserObjectModel.getDocument().createElement('div');
    settingsElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerSettings');

    const cogElement = this.browserObjectModel.getDocument().createElement('span');
    cogElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerCog');
    cogElement.innerHTML = cogIcon;

    settingsElement.appendChild(cogElement);

    const menuItems: Element[] = [];

    const privacyPolicyLinkElement = this.browserObjectModel.getDocument().createElement('a');
    privacyPolicyLinkElement.href = adSlot.getSettings().getPrivacyPolicyUrl()
      ?? adSlot.getSettings().getLinkProperties()?.privacyPolicy?.href
      ?? 'https://www.trendmd.com/privacy';
    privacyPolicyLinkElement.target = adSlot.getSettings().getLinkProperties()?.privacyPolicy?.target ?? '_blank';
    privacyPolicyLinkElement.rel = adSlot.getSettings().getLinkProperties()?.privacyPolicy?.rel ?? '';
    privacyPolicyLinkElement.referrerPolicy = adSlot.getSettings().getLinkProperties()?.privacyPolicy?.referrerPolicy ?? '';
    privacyPolicyLinkElement.textContent = 'Privacy policy';
    privacyPolicyLinkElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerSettingsLinkPrivacyPolicy');

    menuItems.push(privacyPolicyLinkElement);

    const analyticsLinkElement = this.browserObjectModel.getDocument().createElement('a');
    analyticsLinkElement.href = adSlot.getSettings().getLinkProperties()?.analytics?.href
      ?? 'https://www.trendmd.com/google-analytics';
    analyticsLinkElement.target = adSlot.getSettings().getLinkProperties()?.analytics?.target ?? '_blank';
    analyticsLinkElement.rel = adSlot.getSettings().getLinkProperties()?.analytics?.rel ?? '';
    analyticsLinkElement.referrerPolicy = adSlot.getSettings().getLinkProperties()?.analytics?.referrerPolicy ?? '';
    analyticsLinkElement.textContent = 'Google Analytics settings';
    analyticsLinkElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerSettingsLinkAnalytics');

    menuItems.push(analyticsLinkElement);

    const menuListElement = this.browserObjectModel.getDocument().createElement('ul');

    for (const linkElement of menuItems) {
      const listItemElement = this.browserObjectModel.getDocument().createElement('li');
      listItemElement.appendChild(linkElement);
      menuListElement.appendChild(listItemElement);
    }

    const modalElement = this.browserObjectModel.getDocument().createElement('div');
    modalElement.className = this.stylesheetRenderer.getClass(adSlot, 'footerSettingsModal');

    modalElement.appendChild(menuListElement);

    settingsElement.appendChild(modalElement);
    return settingsElement;
  }
}
